import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { FuaAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { FuaFormField } from '../../types/FuaFormField'

/**
 * Narrative subsection adapter for FUA Assessment
 * Handles narrative extraction and transformation
 */
export class NarrativeAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const background = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND
    )?.at(0)
    const systemSummaries = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES
    )?.at(0)
    const skilledNeeds = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS
    )?.at(0)
    const adlNeeds = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS
    )?.at(0)
    const patientServices = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES
    )?.at(0)
    const careCoordination = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION
    )?.at(0)
    const patientGoals = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS
    )?.at(0)
    const clinicalEducation = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION
    )?.at(0)
    const finalNotes = getAnswerValuesFromItems(
      [flatAnswers[FuaAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES]],
      FuaAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES
    )?.at(0)

    return {
      [FuaFormField.NARRATIVE_BACKGROUND]: background,
      [FuaFormField.NARRATIVE_SYSTEM_SUMMARIES]: systemSummaries,
      [FuaFormField.NARRATIVE_SKILLED_NEEDS]: skilledNeeds,
      [FuaFormField.NARRATIVE_ADL_NEEDS]: adlNeeds,
      [FuaFormField.NARRATIVE_PATIENT_SERVICES]: patientServices,
      [FuaFormField.NARRATIVE_CARE_COORDINATION]: careCoordination,
      [FuaFormField.NARRATIVE_PATIENT_GOALS]: patientGoals,
      [FuaFormField.NARRATIVE_CLINICAL_EDUCATION]: clinicalEducation,
      [FuaFormField.NARRATIVE_FINAL_NOTES]: finalNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_BACKGROUND)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND,
        value: this.getFormDataField(FuaFormField.NARRATIVE_BACKGROUND)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_SYSTEM_SUMMARIES)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES,
        value: this.getFormDataField(FuaFormField.NARRATIVE_SYSTEM_SUMMARIES)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_SKILLED_NEEDS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS,
        value: this.getFormDataField(FuaFormField.NARRATIVE_SKILLED_NEEDS)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_ADL_NEEDS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS,
        value: this.getFormDataField(FuaFormField.NARRATIVE_ADL_NEEDS)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_PATIENT_SERVICES)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES,
        value: this.getFormDataField(FuaFormField.NARRATIVE_PATIENT_SERVICES)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_CARE_COORDINATION)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION,
        value: this.getFormDataField(FuaFormField.NARRATIVE_CARE_COORDINATION)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_PATIENT_GOALS)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS,
        value: this.getFormDataField(FuaFormField.NARRATIVE_PATIENT_GOALS)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_CLINICAL_EDUCATION)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION,
        value: this.getFormDataField(FuaFormField.NARRATIVE_CLINICAL_EDUCATION)
      })
    }

    if (this.formDataFieldExists(FuaFormField.NARRATIVE_FINAL_NOTES)) {
      mutations.push({
        linkId: FuaAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES,
        value: this.getFormDataField(FuaFormField.NARRATIVE_FINAL_NOTES)
      })
    }

    return mutations
  }
}
